import React from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  Label,
  Input,
  FormGroup,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { titleCase } from "../../utils/case";
import { LANGS } from "../../utils/pageHelpers";
import { HOME_ICON_OPTIONS, ICON_MAP } from "../../utils/icons";
import { getImageUrl } from "../../utils/image";
import { hasRole } from "../../utils/auth";

const LangInput = ({
  t,
  content,
  field,
  label,
  type = "text",
  updateLangField,
  disabled
}) => (
  <Card className="mb-4">
    <CardBody>
      <h6>{titleCase(t(label))}</h6>

      <Row>
        {LANGS.map((lang) => (
          <Col md="4" key={lang}>
            <FormGroup>
              <Label>{lang.toUpperCase()}</Label>

              <Input
                type={type}
                rows={type === "textarea" ? 4 : undefined}
                value={content[field]?.[lang] || ""}
                onChange={(e) =>
                  updateLangField(field, lang, e.target.value)
                }
                disabled={disabled}
              />
            </FormGroup>
          </Col>
        ))}
      </Row>
    </CardBody>
  </Card>
);

const ArrayLangInput = ({
  t,
  content,
  arrayField,
  index,
  field,
  label,
  type = "text",
  updateArrayLangField,
  disabled
}) => (
  <Row>
    {LANGS.map((lang) => (
      <Col md="4" key={lang}>
        <FormGroup>
          <Label>
            {titleCase(t(label))} - {lang.toUpperCase()}
          </Label>

          <Input
            type={type}
            rows={type === "textarea" ? 3 : undefined}
            value={
              content[arrayField]?.[index]?.[field]?.[lang] || ""
            }
            onChange={(e) =>
              updateArrayLangField(
                arrayField,
                index,
                field,
                lang,
                e.target.value
              )
            }
            disabled={disabled}
          />
        </FormGroup>
      </Col>
    ))}
  </Row>
);

const Home = ({
  content,
  lots = [],
  updateLangField,
  updateArrayField,
  updateArrayLangField,
  handleImageUpload,
}) => {
  const { t } = useTranslation();
  const canManage = hasRole(
    "superadmin",
    "finance"
  );

  return (
    <>
      <Card className="mb-4">
        <CardBody>
          <FormGroup>
            <Label>{titleCase(t("hero image"))}</Label>

            {content.heroImage && (
              <div className="mb-3">
                <img
                  src={getImageUrl(content.heroImage)}
                  alt={titleCase(t("hero image"))}
                  className="img-fluid rounded border"
                  style={{
                    maxHeight: "300px",
                    objectFit: "cover",
                  }}
                />
              </div>
            )}

            {canManage && (
              <Input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  handleImageUpload("heroImage", e.target.files?.[0])
                }
              />
            )}
          </FormGroup>
        </CardBody>
      </Card>

      <LangInput
        t={t}
        content={content}
        field="heroTitle"
        label="hero title"
        updateLangField={updateLangField}
        disabled={!canManage}
      />
      <LangInput
        t={t}
        content={content}
        field="heroContent"
        label="hero content"
        type="textarea"
        updateLangField={updateLangField}
        disabled={!canManage}
      />

      <Card className="mb-4">
        <CardBody>
          <FormGroup>
            <Label>{titleCase(t("intro image"))}</Label>
            {canManage && (
              <Input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  handleImageUpload("introImage", e.target.files[0])
                }
              />
            )}
            {content.introImage && (
              <>
                <div className="mb-3 mt-3">
                  <img
                    src={getImageUrl(content.introImage)}
                    alt={titleCase(t("intro image"))}
                    className="img-fluid rounded border"
                    style={{
                      maxHeight: "300px",
                      objectFit: "cover",
                    }}
                  />
                </div>
              </>
            )}
          </FormGroup>
        </CardBody>
      </Card>

      <LangInput
        t={t}
        content={content}
        field="introLabel"
        label="intro label"
        updateLangField={updateLangField}
        disabled={!canManage}
      />
      <LangInput
        t={t}
        content={content}
        field="introTitle"
        label="intro title"
        updateLangField={updateLangField}
        disabled={!canManage}
      />
      <LangInput
        t={t}
        content={content}
        field="introContent"
        label="intro content"
        type="textarea"
        updateLangField={updateLangField}
        disabled={!canManage}
      />

      <Card className="mb-4">
        <CardBody>
          <h6 className="mb-1">
            {titleCase(t("intro highlights"))}
          </h6>

          <small className="text-muted d-block mb-4">
            {titleCase(
              t(
                "Maximum: 3"
              )
            )}
          </small>

          {(content.introHighlights || []).map((item, index) => (
            <Card className="mb-3" key={index}>
              <CardBody>
                <h6 className="mb-4">
                  {titleCase(t("item"))} {index + 1}
                </h6>
                <FormGroup>
                  <Label>{titleCase(t("icon"))}</Label>

                  <small className="text-muted d-block mb-3">
                    {titleCase(
                      t("click an icon below to select it")
                    )}
                  </small>

                  <div
                    className="d-flex flex-wrap gap-2"
                    style={{ rowGap: "12px" }}
                  >
                    {Object.entries(ICON_MAP).map(
                      ([name, Icon]) => {
                        const active = item.icon === name;

                        return (
                          <button
                            key={name}
                            type="button"
                            className={`border rounded d-flex align-items-center justify-content-center ${
                              active
                                ? "bg-primary"
                                : "bg-white"
                            }`}
                            style={{
                              width: 44,
                              height: 44,
                              padding: 0,
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                            }}
                            onClick={() =>
                              updateArrayField(
                                "introHighlights",
                                index,
                                "icon",
                                name
                              )
                            }
                            title={name.replace("Fa", "")}
                            disabled={!canManage}
                          >
                            <Icon
                              size={18}
                              className={active ? "text-white" : ""}
                            />
                          </button>
                        );
                      }
                    )}
                  </div>
                </FormGroup>

                <ArrayLangInput
                  t={t}
                  content={content}
                  arrayField="introHighlights"
                  index={index}
                  field="title"
                  label="title"
                  updateArrayLangField={updateArrayLangField}
                  disabled={!canManage}
                />

                <ArrayLangInput
                  t={t}
                  content={content}
                  arrayField="introHighlights"
                  index={index}
                  field="description"
                  label="description"
                  type="textarea"
                  updateArrayLangField={updateArrayLangField}
                  disabled={!canManage}
                />
              </CardBody>
            </Card>
          ))}
        </CardBody>
      </Card>

      <Card className="mb-4">
        <CardBody>
          <h6>{titleCase(t("featured lots"))}</h6>

          <small className="text-muted d-block mb-4">
            {titleCase(
              t(
                "lots are displayed in the same order as configured below. featured lot 1 appears first on the homepage."
              )
            )}
          </small>

          {(content.featuredLots || []).map((item, index) => (
            <FormGroup key={index} className="mb-3">
              <Label>
                {titleCase(t("featured lot"))} {index + 1}
                {index === 0 && (
                  <span className="text-warning ms-2">
                    ({titleCase(t("camper's choice"))})
                  </span>
                )}
              </Label>

              <Input
                type="select"
                value={item.lotId || ""}
                onChange={(e) => {
                  const lotId = e.target.value
                    ? Number(e.target.value)
                    : null;

                  updateArrayField(
                    "featuredLots",
                    index,
                    "lotId",
                    lotId
                  );

                  updateArrayField(
                    "featuredLots",
                    index,
                    "sortOrder",
                    index + 1
                  );
                }}
                disabled={!canManage}
              >
                <option value="">
                  {titleCase(t("select lot"))}
                </option>

                {lots
                  .filter((lot) => lot.allowPublicBooking === true)
                  .map((lot) => (
                    <option
                      key={lot.id}
                      value={lot.id}
                      disabled={content.featuredLots?.some(
                        (x, i) =>
                          i !== index &&
                          Number(x.lotId) === lot.id
                      )}
                    >
                      {lot.location} - {lot.name}
                    </option>
                  ))}

              </Input>
            </FormGroup>
          ))}
        </CardBody>
      </Card>

      <LangInput
        t={t}
        content={content}
        field="highlightTitle"
        label="highlight title"
        updateLangField={updateLangField}
        disabled={!canManage}
      />
      <LangInput
        t={t}
        content={content}
        field="highlightDescription"
        label="highlight description"
        type="textarea"
        updateLangField={updateLangField}
        disabled={!canManage}
      />

      <Card className="mb-4">
        <CardBody>
          <h6 className="mb-1">
            {titleCase(t("highlight list"))}
          </h6>

          <small className="text-muted d-block mb-4">
            {titleCase(
              t(
                "maximum"
              )
            )}: 4
          </small>

          {(content.highlightList || []).map((item, index) => (
            <Card className="mb-3" key={index}>
              <CardBody>
                <h6 className="mb-4">
                  {titleCase(t("item"))} {index + 1}
                </h6>
                <FormGroup>
                  <Label>{titleCase(t("icon"))}</Label>

                  <small className="text-muted d-block mb-3">
                    {titleCase(
                      t("click an icon below to select it")
                    )}
                  </small>

                  <div
                    className="d-flex flex-wrap gap-2"
                    style={{ rowGap: "12px" }}
                  >
                    {Object.entries(ICON_MAP).map(
                      ([name, Icon]) => {
                        const active = item.icon === name;

                        return (
                          <button
                            key={name}
                            type="button"
                            className={`border rounded d-flex align-items-center justify-content-center ${
                              active
                                ? "bg-primary"
                                : "bg-white"
                            }`}
                            style={{
                              width: 44,
                              height: 44,
                              padding: 0,
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                            }}
                            onClick={() =>
                              updateArrayField(
                                "highlightList",
                                index,
                                "icon",
                                name
                              )
                            }
                            title={name.replace("Fa", "")}
                            disabled={!canManage}
                          >
                            <Icon
                              size={18}
                              className={active ? "text-white" : ""}
                            />
                          </button>
                        );
                      }
                    )}
                  </div>
                </FormGroup>

                <ArrayLangInput
                  t={t}
                  content={content}
                  arrayField="highlightList"
                  index={index}
                  field="title"
                  label="title"
                  updateArrayLangField={updateArrayLangField}
                  disabled={!canManage}
                />

                <ArrayLangInput
                  t={t}
                  content={content}
                  arrayField="highlightList"
                  index={index}
                  field="description"
                  label="description"
                  type="textarea"
                  updateArrayLangField={updateArrayLangField}
                  disabled={!canManage}
                />
              </CardBody>
            </Card>
          ))}
        </CardBody>
      </Card>
    </>
  );
};

export default Home;